import { useEffect } from 'react';
import { useCalculateLastUpdate } from './useCalculateLastUpdate';
import { setHasUnsavedChanges } from '../../../../reducers/canvasPageStateReducer';
import { useDispatch } from 'react-redux';
import { useAutoSaveContext } from './AutoSaveContext';
import { useFunnelMapAutoSave } from './useFunnelMapAutoSave';
import { useSfFunnelsAutoSave } from './useSfFunnelsAutoSave';
import { useThirdPartyFunnelAutoSave } from './useThirdPartyFunnelAutoSave';

export type FunnelMapSaveData = Pick<
  FunnelMap,
  | 'id'
  | 'funnels'
  | 'name'
  | 'thirdPartyFunnels'
  | 'stickyNotes'
  | 'metadataElements'
  | 'sections'
  | 'texts'
>;

export interface AutoSaveProps {
  funnelMap: FunnelMap;
  readonly?: boolean;
  crmOrgId?: string | null;
  isButtonHidden?: boolean;
}

export const useAutoSave = ({ funnelMap, crmOrgId }: AutoSaveProps) => {
  const dispatch = useDispatch();
  const { dirtyObjectsExists, hasAnyErrors, isSaving } = useAutoSaveContext();
  const { funnelsData, thirdPartyFunnelsData } = funnelMap;

  const { lastUpdated, setLastUpdatedFor } = useCalculateLastUpdate({
    funnelsData,
    funnelMapLastUpdatedAt: funnelMap.updatedAt,
  });

  useEffect(() => {
    dispatch(setHasUnsavedChanges({ hasUnsavedChanges: dirtyObjectsExists }));
  }, [dirtyObjectsExists, dispatch]);

  useFunnelMapAutoSave({
    funnelMap: funnelMap,
    setLastUpdatedFor,
  });

  useSfFunnelsAutoSave({
    funnelsData,
    crmOrgId,
    setLastUpdatedFor,
  });

  useThirdPartyFunnelAutoSave({
    thirdPartyFunnelsData,
    setLastUpdatedFor,
  });

  return {
    isAutoSaving: isSaving,
    autoSavingError: hasAnyErrors,
    updatedAt: lastUpdated ? new Date(lastUpdated) : undefined,
  };
};
